import { useState, useEffect } from "react";
import { Save, X } from "react-feather";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Card, CardContent } from "@mui/material";
import {
  FaAlignCenter,
  FaAlignLeft,
  FaAlignRight,
} from "react-icons/fa";

import Editor, {
  BtnBold,
  BtnBulletList,
  BtnClearFormatting,
  BtnItalic,
  BtnLink,
  BtnNumberedList,
  BtnRedo,
  BtnStrikeThrough,
  BtnStyles,
  BtnUnderline,
  BtnUndo,
  createButton,
  Toolbar
} from "react-simple-wysiwyg";

import Button from "../input/Button";
import Dropdown from "../input/Dropdown";
import Input from "../input/Input";
import LoadingSpinner from "../LoadingSpinner";
import Tab from "../Tab";
import { createReleaseNote,getAllReleaseNotes } from "../../api/releaseNoteApi";

import { emptyValidator, formValidator } from "../../validators";


const AddReleaseNote = ({ setSelectedKey }) => {
  const user = useSelector((state) => state.user);

  const [tab, setTab] = useState(1);

  const [version, setVersion] = useState("");
  const [releaseDate, setReleaseDate] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState(1);
  const [active, setActive] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const [errors, setErrors] = useState({});

  const validationData = {
    version: { name: "Version", validators: [emptyValidator()] },
    releaseDate: { name: "Release Date", validators: [emptyValidator()] },
    title: { name: "Title", validators: [emptyValidator()] },
    description: { name: "Description", validators: [emptyValidator()] },
  };

  const statusOpt = [
    { key: 1, text: "PUBLISHED", value: 1 },
    { key: 2, text: "DRAFT", value: 2 },
  ];

  const activeOpt = [
    { key: 1, text: "ACTIVE", value: 1 },
    { key: 0, text: "INACTIVE", value: 0 },
  ];

  const handleClose = () => setSelectedKey(null);

  const handleGenerateVersion = () => {
    const now = new Date();
    const year = String(now.getFullYear()).slice(2);
    const month = String(now.getMonth() + 1).padStart(2, "0");

    setVersion(`v1.${year}.${month}.01`);
  };

  const handleSave = async () => {
    const formData = { version, releaseDate, title, description };
  
    const validationErrors = formValidator(formData, validationData);
  
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error(Object.values(validationErrors)[0], { containerId: "Modal" });
      return;
    }
  
    try {
      const allNotes = await getAllReleaseNotes();
  
      const isDuplicate = allNotes.some(
        (item) => item.version?.toLowerCase() === version.toLowerCase()
      );
  
      if (isDuplicate) {
        setErrors((prev) => ({
          ...prev,
          version: "Version already exists",
        }));
  
        toast.error("Version already exists", { containerId: "Modal" });
        return;
      }
    } catch (e) {
      console.error("Version check failed", e);
    }
  
    setErrors({});
    setIsLoading(true);
  
    try {
      const payload = {
        version: version.trim(),
        releaseDate,
        title: title.trim(),
        description,
        status,
        active,
        createdBy: user.username,
      };
  
      const res = await createReleaseNote(payload);
  
      if (res) {
        toast.success("Release Note created successfully!", {
          containerId: "Modal",
        });
        setSelectedKey(null);
      }
    } catch (e) {
      toast.error("Error saving release note", {
        containerId: "Modal",
      });
    }
  
    setIsLoading(false);
  };

  return (
    <>
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 1000,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <div className="help-center-Add-header">
            <X
              size={30}
              onClick={handleClose}
              style={{ position: "absolute", right: 20, top: 20, cursor: "pointer" }}
            />
          </div>

          <Card>
            <CardContent>

              <div className="ui tabular menu top attached">
                <Tab Icon={Save} onClick={() => setTab(1)} active={tab === 1} label="Release Note" />
              </div>

              <div className="ui bottom attached active tab segment">
                <div className="ui form">

                  <div style={{ display: "flex", gap: "10px", alignItems: "end" }}>
                    <div style={{ flex: 1 }}>
                      <Input
                        required
                        label="Version"
                        value={version}
                        placeholder="v1.25.07.11"
                        onChange={(e) => setVersion(e.target.value)}
                        error={errors.version}
                      />
                    </div>

                    <Button label="Generate" onClick={handleGenerateVersion} />
                  </div>

                  <div style={{ fontSize: "12px", color: "#888", marginTop: "4px" }}>
                    Format: v[major].[year].[month].[migration]
                  </div>

                  <div className="field">
                    <Input
                      required
                      type="date"
                      label="Release Date"
                      value={releaseDate}
                      onChange={(e) => setReleaseDate(e.target.value)}
                      error={errors.releaseDate}
                    />
                  </div>

                  <div className="field">
                    <Input
                      required
                      label="Title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      error={errors.title}
                    />
                  </div>

                  <div className="field">
                    <label>Description</label>

                    <div
                      style={{
                        border: errors.description ? "1px solid #e0b4b4" : "1px solid #ccc",
                        borderRadius: "4px",
                      }}
                    >
                      <Editor value={description} onChange={(e) => setDescription(e.target.value)}>
                        <Toolbar>
                          <BtnBold />
                          <BtnItalic />
                          <BtnUnderline />
                          <BtnBulletList />
                          <BtnLink />
                          <BtnNumberedList />
                          <BtnRedo />
                          <BtnStrikeThrough />
                          <BtnStyles />
                          <BtnClearFormatting />
                          <BtnUndo />
                        </Toolbar>
                      </Editor>
                    </div>

                    {errors.description && (
                      <div style={{ color: "#9f3a38", fontSize: "12px" }}>
                        {errors.description}
                      </div>
                    )}
                  </div>

                  <div className="two fields">
                    <Dropdown label="Status" options={statusOpt} disabled value={status} onChange={(e, d) => setStatus(d.value)} />
                    <Dropdown label="Active" options={activeOpt} disabled value={active} onChange={(e, d) => setActive(d.value)} />
                  </div>

                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  label="Save"
                  onClick={handleSave}
                  isLoading={isLoading}
                  Icon={Save}
                  disabled={!version}
                />
              </div>

            </CardContent>
          </Card>
        </div>
      </div>

      {isLoading && <LoadingSpinner message="Saving..." />}
    </>
  );
};

export default AddReleaseNote;